import { axiosInstance } from "./axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  IEbiAgent,
  IEbiAgentCreated,
  IEbiAgentGrantRequest,
  IEbiKeyRequestClaim,
  IEbiKeyRequestCompleteResult,
  IEbiKeyRequestsSummary,
  IEbiKeyShareResult,
  IEbiMailPreview,
  IEbiMailTemplate,
  IEbiMailTheme,
  IEbiMailThemeRequest,
  IEbiMailThemeSettings,
  IEbiOfficeConnection,
  IEbiOfficeConnectRequest,
  IEbiOfficeSettings,
  IEbiOfficeSettingsRequest,
  IEbiPortalThemeRequest,
  IEbiPortalThemeSettings,
  IEbiRecipientKey,
  IEbiRegisterRequest,
  IEbiRegisterResponse,
  IEbiSubmissionFormsRequest,
  IEbiSubmissionFormsSettings,
} from "dto/Ebi/IEbiOffice";

// EBI od strony urzędu (zalogowany pracownik). Portal petenta to osobna aplikacja (ebi/, serwis ebi/src/api/ebiService.ts).

const queryKeys = {
  getSettings: "ebiOfficeService.getSettings",
  getConnection: "ebiOfficeService.getConnection",
  getKeyRequestsSummary: "ebiOfficeService.getKeyRequestsSummary",
  getAgents: "ebiOfficeService.getAgents",
  getMailTemplates: "ebiOfficeService.getMailTemplates",
  // klucz z kolorami: podgląd niezapisanych kolorów jest osobnym wpisem (unieważnianie po prefiksie obejmuje wszystkie)
  getMailPreview: (key: string, theme?: IEbiMailTheme | null) => [
    "ebiOfficeService.getMailPreview",
    key,
    theme?.Background ?? "",
    theme?.ButtonColor ?? "",
    theme?.FooterBackground ?? "",
  ],
  getMailTheme: "ebiOfficeService.getMailTheme",
  getPortalTheme: "ebiOfficeService.getPortalTheme",
  getSubmissionForms: "ebiOfficeService.getSubmissionForms",
  getRegister: (request: IEbiRegisterRequest) => ["ebiOfficeService.getRegister", request],
};

const getSettings = (): Promise<{ data: IEbiOfficeSettings }> => {
  return axiosInstance.get("ebi/office/settings");
};

const useGetSettings = (enabled: boolean = true) => {
  return useCustomQuery(queryKeys.getSettings, () => getSettings(), {
    enabled,
    // 400 „tylko w ramach firmy” jest stały — bez ponawiania (menu pyta przy każdym wejściu).
    retry: false,
  });
};

const saveSettings = (
  payload: IEbiOfficeSettingsRequest,
): Promise<{ data: IEbiOfficeSettings }> => {
  return axiosInstance.post("ebi/office/settings", payload);
};

const useSaveSettings = () => {
  return useCustomMutation(saveSettings);
};

// ── Połączenie z platformą EBI (portal petenta poza urzędem) ─────────────

/** Odpowiedź połączenia; nieznana trasa /api/ wraca jako index.html (fallback SPA) — sprawdzamy kształt odpowiedzi. */
const asConnection = (response: { data: IEbiOfficeConnection }): { data: IEbiOfficeConnection } => {
  if (typeof response.data?.IsConnected !== "boolean") {
    throw new Error("Serwer nie zwrócił stanu połączenia z platformą EBI.");
  }

  return response;
};

const getConnection = async (): Promise<{ data: IEbiOfficeConnection }> =>
  asConnection(await axiosInstance.get<IEbiOfficeConnection>("ebi/office/connection", { params: { _: Date.now() } }));

// zleconą synchronizację wykonuje usługa w tle Magistratu — do jej końca odświeżamy stan połączenia
const SYNC_POLL_MS = 3000;

const useGetConnection = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getConnection, () => getConnection(), {
    enabled,
    retry: false,
    refetchInterval: (response) => (response?.data.SyncRequested ? SYNC_POLL_MS : false),
  });

/** Połączenie kodem rejestracyjnym od operatora platformy — administrator albo właściciel firmy. */
const connect = async (payload: IEbiOfficeConnectRequest): Promise<{ data: IEbiOfficeConnection }> =>
  asConnection(await axiosInstance.post<IEbiOfficeConnection>("ebi/office/connection", payload));

const useConnect = () => useCustomMutation(connect);

/** Rozłączenie: portal przestaje dostawać pisma i oddawać wnioski, do ponownego połączenia nowym kodem. */
const disconnect = async (): Promise<{ data: IEbiOfficeConnection }> =>
  asConnection(await axiosInstance.post<IEbiOfficeConnection>("ebi/office/connection/disconnect"));

const useDisconnect = () => useCustomMutation(disconnect);

/** Zlecenie synchronizacji (pisma wychodzące → EBI, wnioski EBI → Magistrat) — wykonuje ją usługa w tle, odpowiedź ma `SyncRequested`. */
const sync = async (): Promise<{ data: IEbiOfficeConnection }> =>
  asConnection(await axiosInstance.post<IEbiOfficeConnection>("ebi/office/connection/sync"));

const useSync = () => useCustomMutation(sync);

/**
 * Kreator przesyłki: bieżące klucze petentów dla adresatów EBI. Podmiot bez PESEL-u nie występuje
 * w odpowiedzi; petent bez klucza ma `PublicKey = null`.
 */
const getRecipientKeys = async (
  participantIds: number[],
): Promise<IEbiRecipientKey[]> => {
  if (!participantIds.length) return [];

  const { data } = await axiosInstance.post<IEbiRecipientKey[]>(
    "ebi/office/recipientKeys",
    { ParticipantIds: participantIds },
  );

  return data ?? [];
};

// ── Etap 4: kolejka przeszyfrowań (KeysManager) ─────────────────────────

/** Przejęcie najstarszego zadania firmy (dzierżawa 5 min); `null` — nic nie czeka (204). */
const claimKeyRequest = async (): Promise<IEbiKeyRequestClaim | null> => {
  const response = await axiosInstance.post<IEbiKeyRequestClaim>(
    "ebi/office/keyRequests/claim",
  );

  return response.status === 204 ? null : response.data ?? null;
};

const completeKeyRequest = async (
  requestId: string,
  keys: IEbiKeyShareResult[],
): Promise<IEbiKeyRequestCompleteResult> => {
  const { data } = await axiosInstance.post<IEbiKeyRequestCompleteResult>(
    `ebi/office/keyRequests/${requestId}/complete`,
    { Keys: keys },
  );

  return data;
};

const failKeyRequest = (requestId: string, reason: string): Promise<unknown> =>
  axiosInstance.post(`ebi/office/keyRequests/${requestId}/fail`, {
    Reason: reason,
  });

const getKeyRequestsSummary = (): Promise<{ data: IEbiKeyRequestsSummary }> =>
  axiosInstance.get("ebi/office/keyRequests/summary");

const useGetKeyRequestsSummary = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getKeyRequestsSummary, () => getKeyRequestsSummary(), {
    enabled,
  });

const retryFailedKeyRequests = (): Promise<unknown> =>
  axiosInstance.post("ebi/office/keyRequests/retry");

const useRetryFailedKeyRequests = () => useCustomMutation(retryFailedKeyRequests);

// ── Usługa przeszyfrowań na serwerze urzędu (ebi-agent) ──────────────────

/**
 * Lista usług firmy. Znacznik czasu w adresie omija pamięć podręczną przeglądarki: serwer bez tej trasy (starszy build)
 * odpowiada na nią stroną HTML z ETag/Last-Modified, którą przeglądarka buforuje heurystycznie na długo i serwuje bez
 * żądania nawet po aktualizacji serwera. Odpowiedź inna niż tablica to błąd, nie „sukces” do zapamiętania.
 */
const getAgents = async (): Promise<{ data: IEbiAgent[] }> => {
  const response = await axiosInstance.get<IEbiAgent[]>("ebi/office/agents", {
    params: { _: Date.now() },
  });

  if (!Array.isArray(response.data)) {
    throw new Error("Serwer nie zwrócił listy usług przeszyfrowań.");
  }

  return response;
};

const useGetAgents = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getAgents, () => getAgents(), { enabled, retry: false });

/** Nowa usługa — odpowiedź zawiera jednorazowy kod rejestracji (widoczny tylko raz). */
const createAgent = (name: string): Promise<{ data: IEbiAgentCreated }> =>
  axiosInstance.post("ebi/office/agents", { Name: name });

const useCreateAgent = () => useCustomMutation(createAgent);

/** Grant klucza firmy dla usługi — zbudowany w tej przeglądarce (utils/ebiAgentGrant). */
const grantAgentKey = ({
  agentId,
  ...payload
}: IEbiAgentGrantRequest & { agentId: string }): Promise<{ data: IEbiAgent }> =>
  axiosInstance.post(`ebi/office/agents/${agentId}/grant`, payload);

const useGrantAgentKey = () => useCustomMutation(grantAgentKey);

const revokeAgent = (agentId: string): Promise<{ data: IEbiAgent }> =>
  axiosInstance.post(`ebi/office/agents/${agentId}/revoke`);

const useRevokeAgent = () => useCustomMutation(revokeAgent);

// ── Powiadomienia: szablony maili EBI z danymi urzędu ────────────────────

const getMailTemplates = async (): Promise<{ data: IEbiMailTemplate[] }> => {
  // jak getAgents: starszy serwer odpowiada stroną HTML, która nie jest listą
  const response = await axiosInstance.get<IEbiMailTemplate[]>("ebi/office/templates", {
    params: { _: Date.now() },
  });

  if (!Array.isArray(response.data)) {
    throw new Error("Serwer nie zwrócił listy szablonów powiadomień.");
  }

  return response;
};

const useGetMailTemplates = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getMailTemplates, () => getMailTemplates(), { enabled, retry: false });

/** Podgląd szablonu; `theme` (wszystkie trzy kolory) pokazuje niezapisane kolory zamiast zapisanych. */
const getMailPreview = async (
  key: string,
  theme?: IEbiMailTheme | null,
): Promise<{ data: IEbiMailPreview }> => {
  const response = await axiosInstance.get<IEbiMailPreview>(
    `ebi/office/templates/${encodeURIComponent(key)}/preview`,
    {
      params: {
        _: Date.now(),
        ...(theme
          ? {
              background: theme.Background,
              buttonColor: theme.ButtonColor,
              footerBackground: theme.FooterBackground,
            }
          : {}),
      },
    },
  );

  if (typeof response.data?.Html !== "string") {
    throw new Error("Serwer nie zwrócił podglądu powiadomienia.");
  }

  return response;
};

const useGetMailPreview = (key: string | null, theme?: IEbiMailTheme | null) =>
  useCustomQuery(
    queryKeys.getMailPreview(key ?? "", theme),
    () => getMailPreview(key ?? "", theme),
    {
      enabled: !!key,
      retry: false,
      keepPreviousData: true,
    },
  );

/** Kolorystyka maili firmy (zapisana albo domyślna) i gotowe warianty; starszy serwer odpowiada stroną HTML bez listy. */
const getMailTheme = async (): Promise<{ data: IEbiMailThemeSettings }> => {
  const response = await axiosInstance.get<IEbiMailThemeSettings>("ebi/office/mailTheme", {
    params: { _: Date.now() },
  });

  if (!Array.isArray(response.data?.Presets)) {
    throw new Error("Serwer nie zwrócił kolorystyki powiadomień.");
  }

  return response;
};

const useGetMailTheme = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getMailTheme, () => getMailTheme(), { enabled, retry: false });

/** Zapis kolorów (wszystkie puste = domyślne) — administrator, przy zapisanych ustawieniach EBI. */
const saveMailTheme = (
  payload: IEbiMailThemeRequest,
): Promise<{ data: IEbiMailThemeSettings }> =>
  axiosInstance.post("ebi/office/mailTheme", payload);

const useSaveMailTheme = () => useCustomMutation(saveMailTheme);

// ── Wygląd: kolory portalu petenta ───────────────────────────────────────

const getPortalTheme = async (): Promise<{ data: IEbiPortalThemeSettings }> => {
  const response = await axiosInstance.get<IEbiPortalThemeSettings>("ebi/office/portalTheme", {
    params: { _: Date.now() },
  });

  if (!Array.isArray(response.data?.Presets)) {
    throw new Error("Serwer nie zwrócił kolorów portalu.");
  }

  return response;
};

const useGetPortalTheme = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getPortalTheme, () => getPortalTheme(), { enabled, retry: false });

/** Zapis kolorów portalu (wszystkie puste = domyślne) — administrator, przy zapisanych ustawieniach EBI. */
const savePortalTheme = (
  payload: IEbiPortalThemeRequest,
): Promise<{ data: IEbiPortalThemeSettings }> =>
  axiosInstance.post("ebi/office/portalTheme", payload);

const useSavePortalTheme = () => useCustomMutation(savePortalTheme);

// ── Wnioski: rodzaje przyjmowane przez urząd ──────────────────────────────

const getSubmissionForms = async (): Promise<{ data: IEbiSubmissionFormsSettings }> => {
  const response = await axiosInstance.get<IEbiSubmissionFormsSettings>("ebi/office/submissionForms", {
    params: { _: Date.now() },
  });

  // nieznana trasa /api/ wraca jako index.html (fallback SPA) — sprawdzamy kształt odpowiedzi
  if (!Array.isArray(response.data?.Codes)) {
    throw new Error("Serwer nie zwrócił wyboru wniosków.");
  }

  return response;
};

const useGetSubmissionForms = (enabled: boolean = true) =>
  useCustomQuery(queryKeys.getSubmissionForms, () => getSubmissionForms(), { enabled, retry: false });

/** Zapis wyboru wniosków (`Codes: null` = domyślny) — administrator, przy zapisanych ustawieniach EBI. */
const saveSubmissionForms = (
  payload: IEbiSubmissionFormsRequest,
): Promise<{ data: IEbiSubmissionFormsSettings }> =>
  axiosInstance.post("ebi/office/submissionForms", payload);

const useSaveSubmissionForms = () => useCustomMutation(saveSubmissionForms);

// ── Etap 6: rejestr EBI ──────────────────────────────────────────────────

const getRegister = (
  request: IEbiRegisterRequest,
): Promise<{ data: IEbiRegisterResponse }> =>
  axiosInstance.post("ebi/office/register", request);

const useGetRegister = (request: IEbiRegisterRequest, enabled: boolean = true) =>
  useCustomQuery(queryKeys.getRegister(request), () => getRegister(request), {
    enabled,
    keepPreviousData: true,
  });

export default {
  queryKeys,
  getSettings,
  useGetSettings,
  saveSettings,
  useSaveSettings,
  getConnection,
  useGetConnection,
  connect,
  useConnect,
  disconnect,
  useDisconnect,
  sync,
  useSync,
  getRecipientKeys,
  claimKeyRequest,
  completeKeyRequest,
  failKeyRequest,
  getKeyRequestsSummary,
  useGetKeyRequestsSummary,
  retryFailedKeyRequests,
  useRetryFailedKeyRequests,
  getAgents,
  useGetAgents,
  createAgent,
  useCreateAgent,
  grantAgentKey,
  useGrantAgentKey,
  revokeAgent,
  useRevokeAgent,
  getMailTemplates,
  useGetMailTemplates,
  getMailPreview,
  useGetMailPreview,
  getMailTheme,
  useGetMailTheme,
  saveMailTheme,
  useSaveMailTheme,
  getPortalTheme,
  useGetPortalTheme,
  savePortalTheme,
  useSavePortalTheme,
  getSubmissionForms,
  useGetSubmissionForms,
  saveSubmissionForms,
  useSaveSubmissionForms,
  getRegister,
  useGetRegister,
};
